// ═══════════════════════════════════════════════════════════════════════
// navigation.jsx — Cross-tab navigation context + clickable EntityRef
// ═══════════════════════════════════════════════════════════════════════
// Pattern:
//   <NavigationProvider value={navApi}>
//     ...children that can call useNavigation() and navApi.goToInterface(id)
//   </NavigationProvider>
//
// And:
//   <EntityRef kind="agent" id="AG-001">Design Assistant</EntityRef>
//   renders a clickable badge that, on click, jumps to the right tab.
// ═══════════════════════════════════════════════════════════════════════

import React, { createContext, useContext } from "react";
import { T, FONTS, pill } from "./theme";

const NavigationContext = createContext({
  // default no-op nav; will be replaced by the real one in App.jsx
  goToInterface: () => {},
  goToAgent:     () => {},
  goToBenefit:   () => {},
  goToFlow:      () => {},
  goToGate:      () => {},
  goToTab:       () => {},
  currentTab:    "table",
});

export function NavigationProvider({ value, children }) {
  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  return useContext(NavigationContext);
}

// ─── EntityRef · the universal clickable cross-link ──────────────────
// Usage:
//   <EntityRef kind="interface" id="IF-001" />            → pill with just the ID
//   <EntityRef kind="agent"     id="AG-001">name</EntityRef>
//   <EntityRef kind="benefit"   id="B-01"   compact />
//
// Renders a pill styled per-entity, with hover affordance and onClick
// routing through useNavigation.
//
// Supported kinds: "interface", "agent", "benefit", "flow", "gate"

const KIND_META = {
  interface: { color: T.cyan, icon: "🔗", goFn: "goToInterface", label: "Interface" },
  agent:     { color: T.green, icon: "🤖", goFn: "goToAgent",     label: "Agent" },
  benefit:   { color: T.gold, icon: "🎯", goFn: "goToBenefit",   label: "Benefit" },
  flow:      { color: T.purple, icon: "🚌", goFn: "goToFlow",      label: "ESB Flow" },
  gate:      { color: T.pink, icon: "🛡️", goFn: "goToGate",      label: "Gate" },
};

export function EntityRef({ kind, id, children, compact = false, title }) {
  const nav = useNavigation();
  const meta = KIND_META[kind];
  if (!meta) return <span>{children || id}</span>;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fn = nav[meta.goFn];
    if (typeof fn === "function") fn(id);
  };

  const label = children || id;

  return (
    <button
      onClick={handleClick}
      title={title || `${meta.label}: ${id} · click to open`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: compact ? "1px 6px" : "2px 8px",
        background: `${meta.color}15`,
        color: meta.color,
        border: `1px solid ${meta.color}40`,
        borderRadius: 10,
        fontSize: compact ? 8 : 9,
        fontFamily: FONTS.mono,
        fontWeight: 600,
        cursor: "pointer",
        textDecoration: "none",
        outline: "none",
        transition: "background .12s, transform .08s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = `${meta.color}28`;
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = `${meta.color}15`;
        e.currentTarget.style.transform = "none";
      }}
    >
      {!compact && <span style={{ fontSize: 8 }}>{meta.icon}</span>}
      <span>{label}</span>
    </button>
  );
}

// ─── EntityRefList · render a list of refs of one kind, with header ──
//
// <EntityRefList kind="agent" ids={agents} max={6} label="Agents touching this" />

export function EntityRefList({ kind, ids = [], max = 999, label, emptyText }) {
  const visible = ids.slice(0, max);
  const more = ids.length - visible.length;
  const meta = KIND_META[kind];

  if (ids.length === 0) {
    return emptyText ? (
      <div style={{ fontSize: 9, color: T.text3, fontStyle: "italic" }}>{emptyText}</div>
    ) : null;
  }

  return (
    <div>
      {label && (
        <div
          style={{
            fontSize: 8,
            color: meta?.color || T.text2,
            letterSpacing: 1,
            textTransform: "uppercase",
            marginBottom: 5,
            fontWeight: 700,
            fontFamily: FONTS.mono,
          }}
        >
          {label} ({ids.length})
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {visible.map((id) => (
          <EntityRef key={id} kind={kind} id={id} compact />
        ))}
        {more > 0 && (
          <span
            style={{
              fontSize: 8,
              color: T.text3,
              padding: "1px 6px",
              fontFamily: FONTS.mono,
            }}
          >
            +{more} more
          </span>
        )}
      </div>
    </div>
  );
}
